import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { TOOLS, canAccessTool } from '@sizzlenz/shared';
import type { ToolAccessContext } from '@sizzlenz/shared';
import { useSession } from '@/lib/auth-client';
import { useFeatures } from '@/lib/hooks/useFeatures';

/**
 * Hook that checks whether the current user can access a tool by its href.
 *
 * Returns `{ allowed, loading }`:
 * - While the session is loading, `loading` is true (don't redirect yet).
 * - Once resolved, `allowed` indicates whether the user satisfies the tool's access rules.
 *
 * If the tool has no access rules or isn't found in TOOLS, access is granted.
 *
 * Usage:
 * ```tsx
 * const { allowed, loading } = useToolAccessGuard('/tools/my-tool');
 * if (loading) return <LoadingSkeleton />;
 * if (!allowed) return <AccessDenied />;
 * ```
 */
export function useToolAccessGuard(toolHref: string) {
  const { data: session, isPending } = useSession();
  const { flags } = useFeatures();

  const result = useMemo(() => {
    const tool = TOOLS.find((t) => t.href === toolHref);

    // Tool not in registry = allow (it's a custom page without access rules)
    if (!tool) {
      return { allowed: true, loading: false };
    }

    // Still loading session — don't make a decision yet
    if (isPending) {
      return { allowed: false, loading: true };
    }

    const ctx: ToolAccessContext = {
      isAuthenticated: !!session?.user,
      isAdmin: session?.user?.isAdmin === true,
      featureFlags: flags,
    };

    return { allowed: canAccessTool(tool, ctx), loading: false };
  }, [toolHref, session, isPending, flags]);

  return result;
}
